import React, {
  useCallback,
  useState,
  useMemo,
  useEffect,
  Fragment,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import { Button, Input, Tooltip, Icon } from 'grab-ui';
import I18n from 'i18n-js';
import cx from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import _every from 'lodash/every';
import _uniq from 'lodash/uniq';
import { ExceptMobile, Mobile } from 'views/responsive';
import { fetchOrderMapping } from 'actions/order-search-fetch';
import OrderSearchDuck from 'ducks/order-search-duck';
import warningTriangleSVG from 'public/images/warning-triangle.svg';
import scribeTrack from 'scribe/scribe-track';
import instrumentationDuck from 'ducks/instrumentation-duck';

import styles from './tracking-texbox.less';

const MAX_VISIBLE_ORDER_COUNT = 10;
const ORDER_ID_REGEX = /^(IN|SD|MS|BK)(-)(\d{1})(-)(\w{20})$/;

export const isValidOrderID = orderID => {
  if (_isEmpty(orderID)) {
    return false;
  }
  const upperOrderID = orderID.toUpperCase();
  return ORDER_ID_REGEX.test(upperOrderID);
};

export const filterValidOrderIDs = orderIDString => {
  if (orderIDString) {
    return _uniq(
      orderIDString.split(/[\s,]+/).filter(orderID => isValidOrderID(orderID)),
    );
  }
  return [];
};

export const combineOrderIDs = orderIDs => orderIDs.join(',');

export const isValidOrderIDs = orderIDs => {
  if (_isEmpty(orderIDs)) {
    return true;
  }
  return _every(orderIDs, orderID => {
    const upperOrderID = orderID.toUpperCase();
    return ORDER_ID_REGEX.test(upperOrderID);
  });
};

const TrackingTextbox = () => {
  const { orderId, isExpired, trackingPageType, trackingSource } = useSelector(
    instrumentationDuck.selectors.instrumentation,
  );
  const location = useLocation();

  const [searchText, setSearchText] = useState('');
  const [trackTextDefaultValue, setTrackTextDefaultValue] = useState('');
  const [isInputValid, setIsInputValid] = useState(true);
  const [exceedNumberOfOrder, setExceedNumberOfOrder] = useState(false);
  const [trackButtonDisabled, setTrackButtonDisabled] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onTextChange = useCallback(e => {
    setSearchText(e.target.value);
  }, []);

  const handleSearchOrderID = useCallback(searchString => {
    setExceedNumberOfOrder(false);
    if (!searchString) {
      dispatch(OrderSearchDuck.creators.setSelectedOrder(null));
      dispatch(OrderSearchDuck.creators.setOrderIDMapping({}));
      navigate(
        {
          search: ``,
        },
        { replace: true },
      );
      return;
    }
    const validOrderIDs = filterValidOrderIDs(searchString);
    if (_isEmpty(validOrderIDs)) {
      dispatch(OrderSearchDuck.creators.setSelectedOrder(null));
      dispatch(OrderSearchDuck.creators.setOrderIDMapping({}));
      setIsInputValid(false);
      return;
    }
    const sliceOrderIDs = validOrderIDs.slice(0, 10);
    setExceedNumberOfOrder(validOrderIDs.length > MAX_VISIBLE_ORDER_COUNT);
    setIsInputValid(true);
    dispatch(fetchOrderMapping(sliceOrderIDs));
    const queryParam = combineOrderIDs(validOrderIDs);
    navigate(
      {
        search: `?ids=${queryParam}`,
      },
      { replace: true },
    );
  }, []);

  const onTrackSearch = useCallback(
    () => {
      handleSearchOrderID(searchText);
    },
    [searchText],
  );

  const onTrackClick = useCallback(
    () => {
      scribeTrack.expressTracking.confirmTracking({
        orderIdDisplayed: orderId,
        trackingPageType,
        trackingSource,
        isTrackingLinkExpired: isExpired,
        isInApp: false,
      });

      onTrackSearch();
    },
    [onTrackSearch, orderId, isExpired, trackingPageType, trackingSource],
  );

  useEffect(
    () => {
      if (!searchText) {
        setIsInputValid(true);
        setTrackButtonDisabled(false);
        return;
      }

      const validOrderIDs = filterValidOrderIDs(searchText);
      setTrackButtonDisabled(_isEmpty(validOrderIDs));
    },
    [searchText],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const queryParam = params.get('ids');
    if (queryParam) {
      setTrackTextDefaultValue(queryParam);
      setSearchText(queryParam);
      setTimeout(() => handleSearchOrderID(queryParam), 300);
    }
  }, []);

  const clearSearchText = useCallback(() => {
    setSearchText('');
  }, []);

  const textboxSuffix = useMemo(
    () => (
      <Tooltip placement={'bottom'}>
        <Icon
          type="close-circle"
          className={cx(styles.clearButton)}
          onClick={clearSearchText}
          aria-label="clear button"
        />
      </Tooltip>
    ),
    [],
  );

  const renderExceedNumberOrder = useMemo(
    () => {
      if (!exceedNumberOfOrder) {
        return null;
      }
      return (
        <div className={styles.exceedWarning}>
          <div className={styles.exceedWarningContent}>
            <span>
              <img src={warningTriangleSVG} alt="warning" />
              {I18n.t('orderSearch.exceedOrderWarning')}
            </span>
          </div>
        </div>
      );
    },
    [exceedNumberOfOrder],
  );

  const renderErrText = useMemo(
    () => {
      if (isInputValid) {
        return null;
      }

      return (
        <div className={styles.errText}>
          <span>{I18n.t('orderSearch.trackingBanner.warningText')}</span>
        </div>
      );
    },
    [isInputValid],
  );

  return (
    <Fragment>
      {renderExceedNumberOrder}
      <div>
        <div className={styles.trackingTextBoxContainer}>
          <Input
            className={cx(styles.trackingTextBox, {
              [styles.trackingTextBoxErr]: !isInputValid,
            })}
            placeholder={I18n.t('orderSearch.trackingBanner.placeHolder')}
            onChange={onTextChange}
            onPressEnter={onTrackSearch}
            value={searchText}
            defaultValue={trackTextDefaultValue}
            suffix={textboxSuffix}
            aria-label="order id input"
          />
          <Mobile>{renderErrText}</Mobile>
          <Button
            disabled={trackButtonDisabled}
            className={styles.trackButton}
            onClick={onTrackClick}
            aria-label="track button"
          >
            {I18n.t('orderSearch.trackingBanner.trackButton')}
          </Button>
        </div>
        <ExceptMobile>{renderErrText}</ExceptMobile>
      </div>
    </Fragment>
  );
};

export default TrackingTextbox;
